<template>
  <v-container v-if="order">
    <v-breadcrumbs
      class="px-0 py-2"
      :items="breadcrumbs"
    />
    <h2>
      搬入予約詳細
    </h2>

    <v-row v-if="order.status && order.rejection_reason">
      <v-col>
        <v-alert
          prominent
          type="error"
        >
          <v-row align="center">
            <v-col class="grow">
              {{ order.rejection_reason }}
            </v-col>
          </v-row>
        </v-alert>
      </v-col>
    </v-row>

    <validation-observer ref="observer">
      <v-card
        flat
        class="rounded-card"
      >
        <v-card-text>
          <!-- 営業担当者 -->
          <v-row>
            <v-col cols="12" md="6">
              <div class="overline">
                営業担当者
              </div>
              <h4>{{ order.sales_staff || '-'  }}</h4>
              <v-divider />
            </v-col>
            <v-col
              v-if="isBilling"
              cols="12"
              md="6"
            >
              <v-checkbox
                v-model="order.not_recorded_sales"
                dense
                disabled
                hide-details="auto"
              >
                <template v-slot:label>
                  <small>売上計上しない</small>
                </template>
              </v-checkbox>
            </v-col>
          </v-row>
          <!-- /営業担当者 -->

          <!-- 搬入業者 -->
          <v-row v-if="order.transport">
            <v-col cols="12" md="6">
              <h4 class="field-label">搬入業者</h4>
              <ul style="list-style: none">
                <li>
                  {{ order.transport.name }}
                </li>
                <li v-if="order.transport.address">
                  住所:<span v-if="order.transport.zip_code">〒{{ order.transport.zip_code }}&nbsp;</span>
                  {{ order.transport.address }}
                </li>
                <li v-if="order.transport.phone_number">
                  電話番号: {{ order.transport.phone_number }}
                </li>
              </ul>
            </v-col>
          </v-row>
          <!-- /搬入業者 -->

          <!-- 運搬先 -->
          <v-row v-if="order.trader">
            <v-col cols="12" md="6">
              <h4 class="field-label">運搬先</h4>
              <ul style="list-style: none">
                <li>
                  {{ order.trader.name }}
                </li>
                <li v-if="order.trader.address">
                  住所:<span v-if="order.trader.zip_code">〒{{ order.trader.zip_code }}&nbsp;</span>
                  {{ order.trader.address }}
                </li>
                <li v-if="order.trader.phone_number">
                  電話番号: {{ order.trader.phone_number }}
                </li>
              </ul>
            </v-col>
            <v-col cols="12" md="6">
              <h4>運搬先事業場</h4>
              <div class="ml-3 mt-1">
                <h4>{{ order.trader.location.name }}</h4>
                <p class="mb-0">
                  <span v-if="order.trader.location.zip_code">〒{{ order.trader.location.zip_code }}&nbsp;</span>
                  {{ order.trader.location.address }}
                </p>
                <p>電話番号:{{ order.trader.location.phone_number || '-' }}</p>
              </div>
            </v-col>
          </v-row>
          <!-- /運搬先 -->

          <!-- 搬入依頼日 -->
          <v-row>
            <v-col cols="12" md="3">
              <div class="overline">
                搬入依頼日
              </div>
              <h4>{{ order.plan_date | date_format }}</h4>
              <v-divider />
            </v-col>
          </v-row>
          <!-- /搬入依頼日 -->

          <!-- 予定時間 -->
          <v-row>
            <v-col>
              <div class="overline">
                予定時間
              </div>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12" md="3">
              <h4>
                <span>
                  {{ order.plan_start_time | from_to_linked_time(order.plan_end_time, ' 〜 ') }}&nbsp;
                </span>
              </h4>
              <v-divider />
            </v-col>
          </v-row>
          <!-- /予定時間 -->

          <!-- 検索キーワード -->
          <v-row>
            <v-col cols="12" md="12">
              <validation-provider
                v-slot="{ errors }"
                name="検索キーワード"
                rules="max:25"
              >
                <v-text-field
                  v-model="order.keyword"
                  :error-messages="errors"
                  :disabled="!['carry_in_accepted', 'carry_allocated', 'carry_transported'].includes(order.status)"
                  dense
                  hide-details="auto"
                  label="検索キーワード"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>検索キーワード</span>
                    <span class="ml-1">
                      ({{ order.keyword?.length || 0 }} / 25)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /検索キーワード -->

          <v-card-text>
            <v-card flat>
              <v-card-actions>
                <v-col
                  v-if="order.is_items"
                  cols="12"
                  md="5"
                >
                  <v-card-title class="text-h6">
                    マニフェスト情報
                  </v-card-title>
                  <!-- マニフェスト情報テーブル -->
                  <v-data-table
                    :headers="manifestTableHeaders"
                    :items="order.order_manifests"
                    :items-per-page="-1"
                    item-key="key"
                    dense
                    disable-sort
                    hide-default-footer
                    style="max-height: 50vh;overflow-y: auto;"
                  >

                    <template v-slot:[`item.manifest_total_weight`]="{ item }">
                      <span v-if="item.manifest_total_weight && item.manifest_total_weight_unit_id">{{ parseFloat(item.manifest_total_weight) | comma_format }}&nbsp;{{ item.manifest_total_weight_unit_name }}</span>
                      <span v-else> {{ "ー" }}</span>
                    </template>
                  </v-data-table>
                  <hr width=”50%″>
                  <v-simple-table dense>
                    <template v-slot:default>
                      <tbody>
                        <tr>
                          <th width="30%">合計数量</th>
                          <td class="text-right" width="35%">
                            <span>{{ manifestTotalCount | comma_format }}</span>
                          </td>
                          <td class="text-right" width="35%">
                            <span v-if="manifestTotalWeight > 0"> {{ manifestTotalWeight | comma_format }}&nbsp;{{ manifestUnitName }}</span>
                            <span v-else> {{ "ー" }}</span>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                  <!-- /マニフェスト情報テーブル -->
                </v-col>
                <v-col
                  cols="12"
                  v-if="isBilling && !order.not_recorded_sales"
                  :md="order.is_items ? 7 : 12"
                >
                  <v-card-title class="text-h6">
                    <validation-provider
                      v-slot="{ errors }"
                      class="requiredMark"
                      name="受注情報"
                      rules="required"
                    >
                      <v-input
                        :value="filteredSalesItems.length > 0 ? 1: null"
                        :error-messages="errors"
                        dense
                        label="受注情報"
                        hide-details="auto"
                        outlined
                      />
                    </validation-provider>
                    <v-spacer />
                    <!-- 搬入受付(受注), (配車済), (運搬完了）-->
                    <v-btn v-if="['carry_in_accepted', 'carry_allocated', 'carry_transported'].includes(order.status)"
                      class="primary"
                      @click="addSalesItemButtonClickEvent"
                    >
                      追加
                    </v-btn>
                  </v-card-title>
                  <!-- 受注情報テーブル -->
                  <v-data-table
                    :headers="order.is_items ? salesTableHeaders : tableHeaders"
                    :items="filteredSalesItems"
                    :items-per-page="-1"
                    item-key="key"
                    dense
                    disable-sort
                    hide-default-footer
                    style="max-height: 50vh;overflow-y: auto;"
                  >
                    <!-- 売上品目コード -->
                    <template v-slot:[`item.sales_item_code`]="{item}">
                      {{ item.sales_item_code }}
                    </template>
                    <!-- /売上品目コード -->
                    <!-- 受入量 -->
                    <template v-slot:[`item.amount_of_acceptance`]="{item}">
                      <span v-if="item.amount_of_acceptance && item.amount_of_acceptance_unit_name">{{ parseFloat(item.amount_of_acceptance) | comma_format }}&nbsp;{{ item.amount_of_acceptance_unit_name }}</span>
                    </template>
                    <!-- /受入量 -->
                    <template v-slot:[`item.editable`]="{ item }" v-if="['carry_in_accepted', 'carry_allocated', 'carry_transported'].includes(order.status)">
                      <v-btn
                        small
                        class="info"
                        @click="editSalesItemClickEvent(item)"
                      >
                        編集
                      </v-btn>
                      &nbsp;
                      <v-btn
                        small
                        class="error"
                        @click="deleteSalesItemClickEvent(item)"
                      >
                        削除
                      </v-btn>
                    </template>
                  </v-data-table>
                  <hr>
                  <v-simple-table dense>
                    <template v-slot:default>
                      <tbody>
                        <tr>
                          <th class="text-right" width="22.2%">受入量合計</th>
                          <td class="text-right" width="22.2%"></td>
                          <td class="text-right" width="22.2%">
                            <span v-if="totalAmountOfAcceptance > 0">{{ totalAmountOfAcceptance | comma_format }}&nbsp;{{ acceptanceUnitName }}</span>
                            <span v-else>{{ "ー" }}</span>
                          </td>
                          <td width="33.4%"></td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                  <!-- /受注情報テーブル -->
                </v-col>
              </v-card-actions>
            </v-card>
          </v-card-text>

        </v-card-text>

        <v-card-actions>
          <transition-manifest-button
            v-if="order.order_manifests.length > 0"
            :order-type="'carry_in_d'"
            :order-id="order.id"
            :request-no="order.request_no"
            :show-confirm="order.status !== 'carry_canceled'"
          />
          <v-spacer />
          <v-btn
            @click="backButtonClickEvent"
          >
            戻る
          </v-btn>
          <v-btn v-if="['carry_in_accepted', 'carry_allocated', 'carry_transported'].includes(order.status)"
            :disabled="order === null"
            class="primary"
            @click="updateButtonClickEvent"
          >
            更新
          </v-btn>

        </v-card-actions>
      </v-card>
    </validation-observer>
    <sales-item-dialog
      :enable-dialog="enableSalesItemDialog"
      :sales-item-prices="salesItemPrices"
      :sales-item="selectedSalesItem"
      :sales-item-units="salesItemUnits"
      @cancelCallback="dialogCancelCallback"
      @registerCallback="dialogRegisterCallback"
    />
    <!-- 売上計上しない にチェックなしの時のconfirm -->
    <registration-provisional-dialog
      :enable-dialog="enableRegistrationProvisionalDialog"
      :from-collection-request="false"
      :from-carryin-pickup-show="true"
      :repeatSetting="'unuse_repeat'"
      @closeCallback="registrationProvisionalDialogCloseCallback"
      @registerCallback="registrationProvisionalRegisterCallback"
    />
    <confirm ref="confirm" />
  </v-container>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import SalesItemDialog from '../../components/_SalesItemDialog'
import RegistrationProvisionalDialog from '../dialogs/_RegistrationProvisional.vue'
import TransitionManifestButton from '../dialogs/_TransitionManifestButton'
import Confirm from '@/components/Confirm'
import _ from 'lodash'

export default {
  name: 'DriverOrderAcceptCarryIn',
  components: {
    SalesItemDialog,
    RegistrationProvisionalDialog,
    TransitionManifestButton,
    Confirm,
  },
  mixins: [],
  props: {},
  data() {
    return {
      breadcrumbs: [
        { text: '受注一覧', disabled: false, to: '/driver/orders/accepts', exact: true },
        { text: '搬入予約詳細', disabled: true, to: '' }
      ],
      order: null,
      enableSalesItemDialog: false,
      enableRegistrationProvisionalDialog: false,
      /** 取引先品目単価 */
      salesItemPrices: [],
      selectedSalesItem: null,
      salesItemUnits: [],
      /** 受注情報 JWNET品目なし */
      tableHeaders: [
        { value: 'sales_item_code', text: '売上品目コード' },
        { value: 'sales_item_name', text: '売上品目名' },
        { value: 'amount_of_acceptance', text: '受入量', align: 'end'},
        { value: 'editable', text: '', align: 'end' },
      ],

      /** 受注情報 JWNET品目あり マニフェスト情報 */
      manifestTableHeaders: [
        { value: 'item_name', text: 'JWNET品目', cellClass: 'tableDisabledField', class: 'tableDisabledField' , width: '30%'},
        { value: 'manifest_total_count', text: 'マニフェスト数', align: 'end', cellClass: 'tableDisabledField', class: 'tableDisabledField' , width: '35%'},
        { value: 'manifest_total_weight', text: 'マニフェスト数量', align: 'end', cellClass: 'tableDisabledField', class: 'tableDisabledField', width: '35%'},
      ],
      /** 受注情報 JWNET品目あり */
      salesTableHeaders: [
        { value: 'sales_item_code', text: '売上品目コード', align: 'end' , width: '22.2%'},
        { value: 'sales_item_name', text: '売上品目名' , width: '22.2%'},
        { value: 'amount_of_acceptance', text: '受入量', align: 'end' , width: '22.2%'},
        { value: 'editable', text: '', align: 'end' , width: '33.4%'},
      ],
      manifestUnitId: null,
      acceptanceUnitId: null,

      manifestUnitName: null,
      acceptanceUnitName: null,

      manifestTotalCount: 0,
      manifestTotalWeight: 0,
      totalAmountOfAcceptance: 0,
    }
  },
  computed: {
    ...mapState('DriverTenantConfiguration', {
      isBilling: 'use_billing',
    }),
    /**
     * フィルターした受注情報リストを返す.
     * 削除ボタン押下により_destroyが設定された行は非表示とする.
     * @return {*[]}
     */
    filteredSalesItems () {
      let filteredList = []
      if (this.order) {
        filteredList = this.order.sales_items.filter(v => !v._destroy)
      }
      return filteredList
    },
  },
  watch: {
    'order.sales_items': function () {
      this.calculateTotalAmount()
    },
  },
  created() {
    this.refreshDetail()
  },
  methods: {
    ...mapActions('DriverOrderAcceptCarryIn', {
      fetchOrder: 'show',
      updateOrder: 'update',
    }),
    /** 取引先品目単価の取得 */
    ...mapActions('DriverMasterSearchSupplierSalesItemPriceDetail', {
      fetchSalesItemPrices: 'index'
    }),
    ...mapActions('DriverMasterSearchSalesItemUnit', {
      fetchSalesItemUnits: 'index',
    }),
    /**
     * 再計算処理
     * すべての受入量の単位が同じ場合、合算する。それ以外は0にして画面で「-」を表示
     */
    calculateTotalAmount() {
      this.manifestTotalCount = 0
      this.manifestTotalWeight = 0
      this.totalAmountOfAcceptance = 0
      if (this.order.is_items) {

        let manifestUnitAllEqual = this.order.order_manifests.every(v => v.manifest_total_weight_unit_name === this.order.order_manifests[0].manifest_total_weight_unit_name)
        if (manifestUnitAllEqual) {

          // マニフェスト数量合計 単位
          this.manifestUnitName = this.order.order_manifests[0].manifest_total_weight_unit_name
          // マニフェスト数量合計
          for (const manifest_item of this.order.order_manifests) {
            this.manifestTotalCount += manifest_item.manifest_total_count
            this.manifestTotalWeight += parseFloat(manifest_item.manifest_total_weight)
          }
        }
      }

      // 受入量単位あり かつ 表示中のリスト
      let isAcceptanceUnitList = this.order.sales_items.filter(v => v.amount_of_acceptance_unit_name !== null && !v._destroy)
      if (isAcceptanceUnitList.length > 0) {

        let acceptanceUnitAllEqual = isAcceptanceUnitList.every(v => v.amount_of_acceptance_unit_name === isAcceptanceUnitList[0].amount_of_acceptance_unit_name)
        if (acceptanceUnitAllEqual) {
          // 受入量合計 単位
          this.acceptanceUnitName = isAcceptanceUnitList[0].amount_of_acceptance_unit_name
          // 受入量合計
          for (const sales_item of isAcceptanceUnitList) {
            this.totalAmountOfAcceptance += parseFloat(sales_item.amount_of_acceptance)
          }
        }
      }
    },
    refreshDetail () {
      this.fetchOrder({ id: this.$route.params.id }).then((content) => {
        this.order = content
        /** 取引先品目単価の取得 */
        this.fetchSalesItemPrices({id: content.transport.id, type: 'Trader', only_jwnet_item: true}).then((content) => {
          this.salesItemPrices = content.sales_item_price_details
        })
      })

      this.fetchSalesItemUnits().then((content) => {
        this.salesItemUnits = content.sales_item_units
      })
    },
    backButtonClickEvent () {
      this.$router.push( { name: 'DriverOrderAccepts' } )
    },
    addSalesItemButtonClickEvent() {
      this.selectedSalesItem = null
      this.enableSalesItemDialog = true
    },
    dialogCancelCallback () {
      this.selectedSalesItem = null
      this.enableSalesItemDialog = false
    },
    dialogRegisterCallback (item) {
        // 追加formのkeyと、order.sales_itemsの保持しているkeyが一緒なら更新

      let detail = this.order.sales_items.find(v => v.key === item.key)
      if (detail) {
        Object.assign(detail, item)
      } else {
        this.order.sales_items.push(item)
      }
      // if (forms.key) {
      //   for (let i = 0; i < this.order.sales_items.length; i++) {
      //     if (this.order.sales_items[i].key === forms.key) {
      //       Object.assign(this.order.sales_items[i], forms)
      //     }
      //   }
      //   // 追加formのkeyに、order.sales_itemsの保持しているkeyがないなら新規作成
      // } else {
      //   forms.key = Math.round( Math.random()*1000000 )
      //   this.order.sales_items.push(forms)
      // }
      this.calculateTotalAmount()
      this.dialogCancelCallback()
    },
    /**
     * 受注情報編集ボタン クリックイベント
     * @param item
     */
    editSalesItemClickEvent (item) {
      this.selectedSalesItem = _.cloneDeep(item)
      this.enableSalesItemDialog = true
    },
    /**
     * 受注情報削除ボタン クリックイベント
     * @param item
     */
    deleteSalesItemClickEvent (item) {
      let modalOption = { title: '削除', message: '削除しますがよろしいですか？', options: { width: 320, titleColor: 'red', isCancelButton: true } }
      this.$refs.confirm.open(modalOption).then((confirm) => {
        this.$refs.confirm.close()
        if (confirm) {
          for (const sales_item of this.order.sales_items) {
            if (sales_item.key === item.key) {
              sales_item._destroy = true
            }
          }
          this.calculateTotalAmount()
        }
      })
    },
    /**
    * 更新ボタン クリックイベント
    */
    updateButtonClickEvent() {
      this.$refs.observer.validate().then((result) => {
        if(result){
          // 通常のconfirm
          if ((this.order.not_recorded_sales) || (this.order.deal.status !== 'unregistered')) {
            let modalOption = { title: '更新', message: 'この内容で更新しますがよろしいですか？', options: { titleColor: 'blue', width: 320, } }
            this.$refs.confirm.open(modalOption).then((confirm) => {
              this.$refs.confirm.close()
              if (confirm) {
                this.registrationProvisionalRegisterCallback(false)
              }
            })
          // 売上入力への遷移を含むconfirm
          } else {
            this.enableRegistrationProvisionalDialog = true
          }
        }
      })
    },
    registrationProvisionalDialogCloseCallback () {
      this.enableRegistrationProvisionalDialog = false
    },
    /**
     * 登録モーダルからの戻り
     * @param afterTransaction { Boolean }　true: 売上入力へ, false: 一覧へ
     */
    registrationProvisionalRegisterCallback (afterTransaction) {
      const params = this.buildParams()
      this.updateOrder({ id: this.order.id, params: params }).then(() => {
        if (afterTransaction) { // OKして売上新規入力へ進むか売上編集に進むか 後で実装
          this.$router.push({name: 'DriverSalesTransactionEdit', params: { id: this.order.deal.id  }})
        } else { // OKして閉じる(一覧画面に戻る)
          this.$router.push({ name: 'DriverOrderAccepts' })
        }
      })
    },
    buildParams () {
      const params = new FormData()
      /* 検索キーワード */
      params.append('keyword', this.nullToBlank(this.order.keyword))
      params.append('order_carryin_attributes[id]', this.nullToBlank(this.order.order_carryin_id))

      if (this.isBilling && !this.order.not_recorded_sales) {
        this.order.sales_items.forEach((sales_item, index) => {
          /* ID */
          params.append(`order_carryin_attributes[order_carryin_details_attributes[${index}][id]`, this.nullToBlank(sales_item.id))
          /* 取引先売上品目単価ID */
          params.append(`order_carryin_attributes[order_carryin_details_attributes[${index}][sales_item_price_code]`, this.nullToBlank(sales_item.sales_item_code))
          /* 売上品目名 */
          params.append(`order_carryin_attributes[order_carryin_details_attributes[${index}][sales_item_price_name]`, this.nullToBlank(sales_item.sales_item_name))
          /* 受入量 */
          params.append(`order_carryin_attributes[order_carryin_details_attributes[${index}][received_quantity]`, this.nullToBlank(sales_item.amount_of_acceptance))
          /* 受入量単位ID */
          params.append(`order_carryin_attributes[order_carryin_details_attributes[${index}][sales_item_unit_id]`, this.nullToBlank(sales_item.amount_of_acceptance_unit_id))

          /* 対象は新規の削除分 */
          if (sales_item._destroy) {
            params.append(`order_carryin_attributes[order_carryin_details_attributes[${index}][_destroy]`, sales_item._destroy)
          }
        })
      }
      return params
    }
  }
}
</script>
